import { useMutation } from '@tanstack/react-query';
import { Loader2Icon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { fillTemplate, formatRelativeDays } from '@/lib/utils';
import { createPortalSession } from '@/lib/api/billing';
import type { BusinessDto } from '@/lib/api/businesses';

import { subscriptionStatusColorClass, subscriptionStatusLabel } from './helpers';

interface SubscriptionStatusCardProps {
  business: BusinessDto;
  tierLabel: string | undefined;
}

export function SubscriptionStatusCard({ business, tierLabel }: SubscriptionStatusCardProps) {
  const { t } = useLanguage();
  const { subscription_status, trial_ends_at, billing_cycle, stripe_customer_id } = business;

  const portalMutation = useMutation({
    mutationFn: () => createPortalSession(business.id),
    meta: { errorMessage: t.billingPortalError },
    onSuccess: (data) => {
      window.location.href = data.portal_url;
    },
  });

  return (
    <section
      className="flex flex-col gap-3 rounded-xl border bg-card p-5"
      data-testid="billing-status"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{tierLabel ?? t.billingCurrentPlan}</span>
          <span
            className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${subscriptionStatusColorClass(
              subscription_status,
            )}`}
            data-testid="billing-status-pill"
          >
            {subscriptionStatusLabel(subscription_status, t)}
          </span>
        </div>

        {stripe_customer_id && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={portalMutation.isPending}
            onClick={() => portalMutation.mutate()}
            data-testid="billing-manage"
          >
            {portalMutation.isPending && <Loader2Icon className="w-3.5 h-3.5 animate-spin" />}
            {t.billingManagePlan}
          </Button>
        )}
      </div>

      {billing_cycle && (
        <p className="text-xs text-muted-foreground">
          {billing_cycle === 'annual' ? t.billingCycleAnnual : t.billingCycleMonthly}
        </p>
      )}

      {subscription_status === 'trialing' && trial_ends_at && (
        <p className="text-sm border-t pt-3">
          {fillTemplate(t.billingTrialBannerText, { when: formatRelativeDays(trial_ends_at) })}
        </p>
      )}

      {subscription_status === 'past_due' && (
        <p className="text-sm text-amber-700 dark:text-amber-400 border-t pt-3">
          {t.billingPastDueNotice}
        </p>
      )}

      {subscription_status === 'canceled' && (
        <p className="text-sm text-destructive border-t pt-3">{t.billingCanceledNotice}</p>
      )}

      {!stripe_customer_id && subscription_status !== 'trialing' && (
        <p className="text-xs text-muted-foreground">{t.billingManagePlanLocked}</p>
      )}
    </section>
  );
}
